import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Grid,
  TextField,
  Typography,
} from '@material-ui/core';
import type { FC } from 'react';
import { useState } from 'react';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { notesApi } from 'src/api/notesApi';
import { useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { userApi } from 'src/api/userApi';
import {
  ClassicEditor,
  AutoImage,
  BlockQuote,
  Bold,
  Essentials,
  Heading,
  ImageBlock,
  ImageCaption,
  ImageInline,
  ImageResize,
  ImageStyle,
  ImageTextAlternative,
  ImageToolbar,
  ImageUpload,
  Indent,
  IndentBlock,
  Italic,
  Link,
  List,
  ListProperties,
  Paragraph,
  SimpleUploadAdapter,
  TextTransformation,
  Table,
  TableToolbar,
  MediaEmbed,
} from 'ckeditor5';
import 'ckeditor5/ckeditor5.css';

type AddNotesProps = {
  setAddNotes: any;
};

const AddNotes: FC<any> = ({ setAddNotes }: AddNotesProps) => {
  const [noteTitle, setNoteTitle] = useState<string>('');
  const [noteDescription, setNoteDescription] = useState<string>('');
  const { organizationID, projectID } = useParams();

  function uploadAdapter(loader) {
    return {
      upload: () =>
        new Promise((resolve) => {
          loader.file.then(async (file) => {
            const formData = new FormData();
            formData.append('file', file);
            const image = await userApi.uploadAttachments(formData);
            resolve({
              default: `${image?.data?.blob_url}`,
            });
          });
        }),
    };
  }
  function uploadPlugin(editor) {
    editor.plugins.get('FileRepository').createUploadAdapter = (loader) =>
      uploadAdapter(loader);
  }

  const editorConfig = {
    toolbar: {
      items: [
        '|',
        'heading',
        '|',
        'bold',
        'italic',
        '|',
        'link',
        'blockQuote',
        'insertImage',
        'undo',
        'redo',
        '|',
        'bulletedList',
        'numberedList',
        'outdent',
        'indent',
        'insertTable',
        'mediaEmbed',
      ],
      shouldNotGroupWhenFull: false,
    },
    plugins: [
      AutoImage,
      BlockQuote,
      Bold,
      Essentials,
      Heading,
      ImageBlock,
      ImageCaption,
      ImageInline,
      ImageResize,
      ImageStyle,
      ImageTextAlternative,
      ImageToolbar,
      ImageUpload,
      Indent,
      IndentBlock,
      Italic,
      Link,
      List,
      ListProperties,
      Paragraph,
      SimpleUploadAdapter,
      TextTransformation,
      Table,
      TableToolbar,
      MediaEmbed,
    ],
    blockToolbar: [
      'bold',
      'italic',
      '|',
      'link',
      '|',
      'bulletedList',
      'numberedList',
      'outdent',
      'indent',
    ],
    heading: {
      options: [
        {
          model: 'paragraph',
          title: 'Paragraph',
          class: 'ck-heading_paragraph',
        },
        {
          model: 'heading1',
          view: 'h1',
          title: 'Heading 1',
          class: 'ck-heading_heading1',
        },
        {
          model: 'heading2',
          view: 'h2',
          title: 'Heading 2',
          class: 'ck-heading_heading2',
        },
        {
          model: 'heading3',
          view: 'h3',
          title: 'Heading 3',
          class: 'ck-heading_heading3',
        },
      ],
    },
    image: {
      toolbar: [
        'toggleImageCaption',
        'imageTextAlternative',
        '|',
        'imageStyle:inline',
        'imageStyle:wrapText',
        'imageStyle:breakText',
        '|',
        'resizeImage',
      ],
    },
    table: {
      contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells'],
    },
    link: {
      addTargetToExternalLinks: true,
      defaultProtocol: 'https://',
      decorators: {
        toggleDownloadable: {
          mode: 'manual',
          label: 'Downloadable',
          attributes: {
            download: 'file',
          },
        },
      },
    },
    list: {
      properties: {
        styles: true,
        startIndex: true,
        reversed: true,
      },
    },

    extraPlugins: [uploadPlugin],
    placeholder: 'Enter your description here',
  };

  const handleCreateNotes = async () => {
    const payload = {
      title: noteTitle,
      text: noteDescription,
      organizationId: organizationID,
      projectId: projectID,
    };

    try {
      const response = await notesApi.createNote(payload);
      if (response?.status === 201) {
        setAddNotes(false);
        toast.success('Note Created');
      }
    } catch (error) {
      toast.error(`${error}`);
    }
  };

  return (
    <div className="notesInner">
      <Accordion defaultExpanded className="addMeetingCard">
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          className="accordionSummary"
          aria-controls="panel1a-content"
          id="panel1a-header">
          <Typography sx={{ fontWeight: 'bold' }}>Add Note</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={4}>
            <Grid item md={12}>
              <Typography>Title</Typography>
              <TextField
                placeholder="Enter Title"
                fullWidth
                inputProps={{ maxLength: 225 }}
                value={noteTitle}
                onChange={(e) => setNoteTitle(e.target.value)}
              />
            </Grid>
            <Grid item md={12}>
              <div className="ckEditor">
                <Typography>Description</Typography>
                <CKEditor
                  editor={ClassicEditor}
                  data={noteDescription}
                  onChange={(event, editor) => {
                    const data = editor.getData();
                    setNoteDescription(data);
                  }}
                  config={editorConfig as any}
                />
              </div>
            </Grid>
          </Grid>

          <Button
            className="createButton"
            variant="contained"
            color="secondary"
            onClick={handleCreateNotes}>
            Create
          </Button>
        </AccordionDetails>
      </Accordion>
    </div>
  );
};

export default AddNotes;
